/**
 * Endpoints de **Reportes** (`/api/reportes/*`).
 *
 * El índice reúne los seis reportes declarados como módulo y su estado; el
 * primer reporte migrado es el **historial de cambios de precios**
 * (`Reportes::historial_precios` + `Historial_precio_model`).
 */

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

const CLAVE = 'reportes';

/** Reporte del índice (los módulos declarados como `reportes.*`). */
export interface ReporteDelIndice {
  codigo: string;
  nombre: string;
  descripcion: string | null;
  ruta: string;
  web_path: string | null;
  /** Ya vive en el panel nuevo (tiene pantalla propia). */
  migrado: boolean;
}

/** Cambio de precio del historial. */
export interface CambioPrecio {
  id: number;
  producto_id: number;
  producto: string;
  tipo: string | null;
  precio_anterior: number | null;
  precio_nuevo: number | null;
  diferencia: number | null;
  porcentaje_cambio: number | null;
  precio_compra_anterior: number | null;
  precio_compra_nuevo: number | null;
  usuario_id: number;
  usuario: string;
  motivo: string | null;
  ip: string | null;
  fecha: string | null;
  hora: string | null;
  cambio: 'AUMENTO' | 'DISMINUCIÓN' | 'SIN CAMBIO';
}

/** Resumen del historial (las tarjetas del panel anterior). */
export interface ResumenHistorial {
  total: number;
  productos: number;
  usuarios: number;
  aumentos: number;
  disminuciones: number;
  sin_cambio: number;
  variacion_media: number | null;
  variacion_maxima: number | null;
  variacion_minima: number | null;
}

/** Filtros del historial de precios. */
export interface FiltrosHistorial {
  desde: string;
  hasta: string;
  tipo: string;
  usuario_id: number | null;
  cambio: string;
  busqueda: string;
}

/** Respuesta del historial de precios. */
export interface HistorialPreciosRespuesta {
  cambios: CambioPrecio[];
  resumen: ResumenHistorial;
  filtros: { desde: string; hasta: string; tipo: string | null; usuario_id: number | null };
  generado_at: string;
}

/** Catálogos del reporte. */
export interface CatalogosHistorial {
  tipos: string[];
  usuarios: { id: number; nombre: string; cambios: number }[];
  rango: { desde: string | null; hasta: string | null; total: number };
  cambios: { valor: string; etiqueta: string }[];
}

/** Fila del listado de documentos (una línea de un comprobante). */
export interface LineaDocumento {
  fecha: string | null;
  /** Origen del comprobante: BOLETA, FACTURA o ADMINISTRACION. */
  origen: string;
  comprobante_id: number;
  serie: string | null;
  numero: string | null;
  documento: string | null;
  cliente: string | null;
  base: number | null;
  igv: number | null;
  total: number | null;
  metodo_pago: string | null;
  producto: string | null;
  precio: number | null;
  cantidad: number | null;
  total_linea: number | null;
}

/** Totales del listado (el pie de la tabla anterior). */
export interface ResumenDocumentos {
  documentos: number;
  clientes: number;
  lineas: number;
  base: number;
  igv: number;
  total: number;
  cantidad: number;
  total_linea: number;
}

/** Filtros del listado de documentos. */
export interface FiltrosDocumentos {
  periodo: string;
  tipo: string;
  series: string[];
}

/** Respuesta del listado de documentos. */
export interface DocumentosRespuesta {
  documentos: LineaDocumento[];
  resumen: ResumenDocumentos;
  filtros: { periodo: string; descripcion: string; tipo: string; series: string[] };
  generado_at: string;
}

/** Catálogos del listado de documentos. */
export interface CatalogosDocumentos {
  series: { factura: string[]; boleta: string[] };
  meses: { periodo: string; documentos: number }[];
  rango: { desde: string | null; hasta: string | null; meses: number };
  tipos: { valor: string; etiqueta: string }[];
  generado_at: string;
}

/** Fila del reporte de ventas de productos (una línea de `venta_producto`). */
export interface LineaVentaProducto {
  fecha: string | null;
  /** Comprobante de la línea: FACTURA, BOLETA, ADMINISTRACION, NOTA DE VENTA o NOTA DE CREDITO. */
  origen: string;
  comprobante_id: number;
  serie: string | null;
  numero: string | null;
  documento: string | null;
  cliente: string | null;
  base: number | null;
  igv: number | null;
  total: number | null;
  cantidad: number | null;
  metodo_pago: string | null;
  producto: string | null;
  producto_id: number | null;
  tipo_producto: string | null;
  /** Precio de lista del producto (el que usaba el `total_venta` anterior). */
  precio: number | null;
  /** Precio realmente aplicado en la línea. */
  precio_unidad: number | null;
  /** Precio de lista por cantidad (la columna del panel anterior). */
  total_venta: number | null;
  /** Precio aplicado por cantidad (lo que se cobró en la línea). */
  total_linea: number | null;
  estado_documento: string | null;
}

/** Totales del reporte de ventas de productos (el pie de la tabla anterior). */
export interface ResumenVentaProducto {
  documentos: number;
  clientes: number;
  productos: number;
  lineas: number;
  base: number;
  igv: number;
  total: number;
  cantidad: number;
  total_venta: number;
  total_linea: number;
}

/** Las mismas líneas agrupadas por producto. */
export interface VentaPorProducto {
  producto_id: number;
  producto: string;
  tipo: string;
  lineas: number;
  cantidad: number;
  total: number;
  total_venta: number;
}

/** Filtros del reporte de ventas de productos. */
export interface FiltrosVentaProductos {
  periodo: string;
  /** `0` (o `*`) para todos los productos. */
  producto: string;
  comprobante: string;
  series: string[];
}

/** Respuesta del reporte de ventas de productos. */
export interface VentaProductosRespuesta {
  lineas: LineaVentaProducto[];
  resumen: ResumenVentaProducto;
  por_producto: VentaPorProducto[];
  filtros: {
    periodo: string;
    descripcion: string;
    producto: number | null;
    comprobante: string;
    series: string[];
  };
  generado_at: string;
}

/** Catálogos del reporte de ventas de productos. */
export interface CatalogosVentaProductos {
  productos: { id: number; nombre: string; tipo: string; precio_venta: number; activo: boolean }[];
  series: { factura: string[]; boleta: string[] };
  meses: { periodo: string; lineas: number }[];
  rango: { desde: string | null; hasta: string | null; meses: number };
  comprobantes: { valor: string; etiqueta: string }[];
  generado_at: string;
}

/** Carga de consumo interno (`internal_consumption`, con el despacho del APP). */
export interface RegistroConsumoInterno {
  id: number;
  fecha: string;
  dia: string;
  cliente: string | null;
  documento: string | null;
  tipo_documento: string | null;
  placa: string | null;
  kilometraje: string | null;
  observaciones: string | null;
  producto: string;
  lado: string;
  volumen: number;
  importe: number;
  transaccion: string | null;
  usuario: string | null;
  /** El registro es una carga con combustible (los demás no cuentan en los totales). */
  es_carga: boolean;
}

/** Totales del consumo interno. */
export interface ResumenConsumoInterno {
  registros: number;
  cargas: number;
  volumen: number;
  importe: number;
  clientes: number;
  vehiculos: number;
  productos: number;
  sin_combustible: number;
}

/** Filtros del consumo interno. */
export interface FiltrosConsumoInterno {
  desde: string;
  hasta: string;
  /** Nombre del combustible (vacío = todos). */
  producto: string;
  /** Placa del vehículo (vacío = todas). */
  placa: string;
}

/** Respuesta del reporte de consumo interno. */
export interface ConsumoInternoRespuesta {
  registros: RegistroConsumoInterno[];
  resumen: ResumenConsumoInterno;
  por_producto: { producto: string; cargas: number; volumen: number; importe: number }[];
  por_dia: { fecha: string; cargas: number; volumen: number; importe: number }[];
  filtros: { desde: string; hasta: string; producto: string | null; placa: string | null };
  generado_at: string;
}

/** Catálogos del consumo interno (productos, meses y rango de datos). */
export interface CatalogosConsumoInterno {
  productos: string[];
  meses: { periodo: string; registros: number }[];
  rango: { desde: string | null; hasta: string | null; meses: number };
  generado_at: string;
}

/** Placa registrada con su cliente (tabla `placa` del panel o `customer_vehicles` del APP). */
export interface PlacaCliente {
  placa: string;
  descripcion: string;
  tipo: string;
  cliente: string | null;
  documento: string | null;
  cliente_id: number | null;
  origen: 'WEB' | 'APP';
}

/** Totales del buscador de placas. */
export interface ResumenPlacas {
  placas: number;
  clientes: number;
  web: number;
  app: number;
  con_varias: number;
}

/** Filtros del buscador de placas. */
export interface FiltrosPlacas {
  busqueda: string;
  /** `WEB`, `APP` o vacío para las dos. */
  origen: string;
}

/** Respuesta del buscador de placas. */
export interface PlacasRespuesta {
  placas: PlacaCliente[];
  resumen: ResumenPlacas;
  por_cliente: {
    cliente_id: number;
    cliente: string;
    documento: string | null;
    placas: number;
    detalle: string[];
  }[];
  filtros: { busqueda: string | null; origen: string | null };
  generado_at: string;
}

/** Catálogos del buscador de placas (orígenes y tipos). */
export interface CatalogosPlacas {
  origenes: { valor: string; etiqueta: string }[];
  tipos: { tipo: string; placas: number }[];
  resumen: { web: number; app: number };
  generado_at: string;
}

/** Registro de la bitácora IZIPAY (`pos_logs`). */
export interface RegistroBitacora {
  id: number;
  timestamp: string;
  fecha: string;
  hora: string;
  usuario: string | null;
  dispositivo: string | null;
  plataforma: string | null;
  version: string | null;
  equipo: string | null;
  /** Operación tal como la registra el punto de venta (con su prefijo). */
  operacion: string;
  /** Operación sin `POS_OPERATION_` ni `START_`, como se muestra. */
  operacion_nombre: string;
  nivel: string | null;
  tipo: string | null;
  monto: number | null;
  moneda: string | null;
  orden: string | null;
  referencia: string | null;
  transaccion: string | null;
  con_error: boolean;
}

/** Registro con sus JSON de detalle. */
export interface DetalleBitacora extends RegistroBitacora {
  parametros: unknown;
  resultado: unknown;
  error: unknown;
}

/** Tarjetas de la bitácora (las mismas del panel anterior). */
export interface ResumenBitacora {
  total: number;
  exitosas: number;
  errores: number;
  advertencias: number;
  monto: number;
  usuarios: number;
  dispositivos: number;
  dias: number;
  tasa_exito: number;
}

/** Filtros de la bitácora. */
export interface FiltrosBitacora {
  desde: string;
  hasta: string;
  operacion: string;
  nivel: string;
  busqueda: string;
  limite: number;
  desplazamiento: number;
}

/** Respuesta de la bitácora. */
export interface BitacoraRespuesta {
  filas: RegistroBitacora[];
  resumen: ResumenBitacora;
  por_operacion: { operacion: string; registros: number; errores: number; monto: number }[];
  por_dia: { fecha: string; registros: number; errores: number; monto: number }[];
  paginacion: { total: number; limite: number; desplazamiento: number; filas: number };
  filtros: {
    desde: string;
    hasta: string;
    operacion: string | null;
    nivel: string | null;
    busqueda: string | null;
  };
  generado_at: string;
}

/** Catálogos de la bitácora (operaciones, niveles, meses y rango de datos). */
export interface CatalogosBitacora {
  operaciones: { operacion: string; registros: number }[];
  niveles: { valor: string; etiqueta: string }[];
  meses: { periodo: string; registros: number }[];
  rango: { desde: string | null; hasta: string | null; dias: number };
  generado_at: string;
}

/** Reparto de un rango por día y por trabajador (el detalle de los dos reportes). */
export interface RepartoDelPeriodo {
  por_dia: { fecha: string; movimientos: number; total: number }[];
  por_trabajador: { trabajador: string; movimientos: number; total: number }[];
}

/** Línea del reporte de ventas y descuentos (punto de venta o crédito). */
export interface LineaVentaDescuento {
  fecha: string | null;
  comprobante: string | null;
  serie: string | null;
  tipo_documento: string;
  fuente: string;
  tipo_descuento: string;
  naturaleza_ajuste: 'descuento' | 'recargo';
  cliente: string | null;
  documento: string | null;
  cliente_id: number;
  placa: string | null;
  producto: string | null;
  producto_id: number | null;
  /** `producto.tipo`: COMBUSTIBLE, LUBRICANTE o COMBO. */
  tipo_producto: string | null;
  /** Precio de lista del producto (el de catálogo). */
  precio_lista: number | null;
  cantidad: number | null;
  precio_unidad: number | null;
  subtotal: number | null;
  total: number | null;
  descuento: number | null;
  /** Método de pago de la venta (el crédito viaja como `CREDITO`). */
  metodo_pago: string | null;
  /** Tarjeta bonus usada en la venta, cuando la venta es con bonus. */
  tarjeta_bonus: string | null;
  grifero: string | null;
  estado: string | null;
  /** El comprobante está anulado: el importe viaja en cero. */
  anulado: boolean;
}

/** Totales del reporte (el pie de las tablas anteriores). */
export interface ResumenVentasDescuentos {
  lineas: number;
  comprobantes: number;
  clientes: number;
  anuladas: number;
  cantidad: number;
  subtotal: number;
  total: number;
  descuento: number;
}

/** Las líneas agrupadas por cliente. */
export interface VentaDescuentoPorCliente {
  documento: string | null;
  cliente: string;
  comprobantes: number;
  lineas: number;
  cantidad: number;
  subtotal: number;
  total: number;
  descuento: number;
}

/** Las líneas agrupadas por producto. */
export interface VentaDescuentoPorProducto {
  producto_id: number;
  producto: string;
  lineas: number;
  cantidad: number;
  total: number;
  descuento: number;
}

/** Filtros del reporte de ventas y descuentos. */
export interface FiltrosVentasDescuentos {
  desde: string;
  hasta: string;
  /** RUC o DNI del cliente (vacío = todos). */
  numero_doc: string;
  /** `todos`, `documentos` (facturas y boletas) o `interno` (notas y créditos). */
  familia: string;
  fuentes: string[];
  tipos: string[];
  /** Sólo las líneas con descuento distinto de cero. */
  solo_descuentos: boolean;
  /**
   * Sólo los comprobantes vigentes (las notas de venta activas): la ficha
   * «Reporte nota venta por cliente» abre la pantalla con `?familia=notas&activos=1`.
   */
  activos: boolean;
  /**
   * Sólo las líneas que no son combustible: la ficha «Reporte de extras en ventas»
   * abre la pantalla con `?extras=1`.
   */
  extras: boolean;
  /**
   * Método de pago de la venta (por ejemplo `BONUS`): la ficha «Reporte de ventas
   * por cliente (bonus)» abre la pantalla con `?metodo_pago=BONUS`.
   */
  metodo_pago: string;
}

/** Respuesta del reporte de ventas y descuentos. */
export interface VentasDescuentosRespuesta {
  lineas: LineaVentaDescuento[];
  resumen: ResumenVentasDescuentos;
  por_cliente: VentaDescuentoPorCliente[];
  por_producto: VentaDescuentoPorProducto[];
  filtros: {
    desde: string;
    hasta: string;
    numero_doc: string | null;
    familia: string;
    fuentes: string[];
    tipos: string[];
    solo_descuentos: boolean;
    activos: boolean;
  };
  generado_at: string;
}

/** Catálogos del reporte (clientes del rango, familias, fuentes, tipos y meses). */
export interface CatalogosVentasDescuentos {
  clientes: { documento: string | null; cliente: string; lineas: number; total: number }[];
  clientes_totales: number;
  familias: { valor: string; etiqueta: string }[];
  fuentes: { valor: string; etiqueta: string }[];
  tipos: { valor: string; etiqueta: string }[];
  meses: { periodo: string; comprobantes: number }[];
  rango: { desde: string | null; hasta: string | null; meses: number };
  generado_at: string;
}

/** Fila del reporte de gastos (un gasto de la tabla `gastos`). */
export interface FilaGasto {
  id: number;
  fecha: string | null;
  /** `gastos.cantidad`: el importe del gasto. */
  total: number | null;
  descripcion: string | null;
  tipo_gasto: number;
  tipo: string;
  turno_id: number | null;
  /** Trabajador del turno (o el que registró el gasto si el turno ya no existe). */
  trabajador: string | null;
  registrado_por: string | null;
  hora_llegada: string | null;
  hora_salida: string | null;
}

/** Totales del reporte de gastos. */
export interface ResumenGastos {
  gastos: number;
  total: number;
  dias: number;
  trabajadores: number;
  promedio: number;
  mayor: number;
  operativo: number;
  administrativo: number;
}

/** Filtros del reporte de gastos. */
export interface FiltrosGastos {
  desde: string;
  hasta: string;
  /** `1` operativos, `2` administrativos o vacío para todos. */
  tipo: string;
  /** Id del trabajador, o vacío para todos. */
  usuario: string;
}

/** Respuesta del reporte de gastos. */
export interface GastosRespuesta extends RepartoDelPeriodo {
  gastos: FilaGasto[];
  resumen: ResumenGastos;
  filtros: { desde: string; hasta: string; tipo: string | null; usuario: number | null };
  generado_at: string;
}

/** Catálogos del reporte de gastos (trabajadores, tipos y meses con gastos). */
export interface CatalogosGastos {
  trabajadores: { id: number; nombre: string; movimientos: number }[];
  tipos: { valor: string; etiqueta: string }[];
  meses: { periodo: string; movimientos: number }[];
  rango: { desde: string | null; hasta: string | null; dias: number };
  generado_at: string;
}

/** Fila del reporte de ingreso a bóveda (una entrada de la tabla `boveda`). */
export interface FilaIngresoBoveda {
  id: number;
  /** Fecha en que se registró la entrada. */
  fecha: string | null;
  /** Fecha del turno (el criterio del rango del reporte). */
  fecha_turno: string | null;
  total: number | null;
  observacion: string | null;
  status: string | null;
  turno_id: number | null;
  trabajador: string | null;
  hora_llegada: string | null;
  hora_salida: string | null;
}

/** Totales del reporte de ingreso a bóveda. */
export interface ResumenIngresoBoveda {
  ingresos: number;
  total: number;
  dias: number;
  trabajadores: number;
  turnos: number;
  promedio: number;
  mayor: number;
}

/** Filtros del reporte de ingreso a bóveda. */
export interface FiltrosIngresoBoveda {
  desde: string;
  hasta: string;
  /** Id del trabajador, o vacío para todos. */
  usuario: string;
}

/** Respuesta del reporte de ingreso a bóveda. */
export interface IngresoBovedaRespuesta extends RepartoDelPeriodo {
  bovedas: FilaIngresoBoveda[];
  resumen: ResumenIngresoBoveda;
  filtros: { desde: string; hasta: string; usuario: number | null };
  generado_at: string;
}

/** Catálogos del reporte de ingreso a bóveda. */
export interface CatalogosIngresoBoveda {
  trabajadores: { id: number; nombre: string; movimientos: number }[];
  meses: { periodo: string; movimientos: number }[];
  rango: { desde: string | null; hasta: string | null; dias: number };
  generado_at: string;
}

/** Fila del reporte de compras (una línea de `compra_producto`). */
export interface LineaCompra {
  compra_id: number;
  serie: string | null;
  numero: number | string | null;
  comprobante: string | null;
  fecha_emision: string | null;
  fecha_vencimiento: string | null;
  tipo_comprobante: string | null;
  ruc_proveedor: string | null;
  proveedor: string | null;
  producto_id: number | null;
  producto: string | null;
  codigo: string | null;
  cantidad: number | null;
  precio_unidad: number | null;
  basico: number | null;
  dscto: number | null;
  isc: number | null;
  rod: number | null;
  igv: number | null;
  precio_flete: number | null;
  precio_total: number | null;
  total: number | null;
  moneda: string | null;
  orden_compra: string | null;
  scop: string | null;
  guia: string | null;
  estado: string;
  estado_codigo: number | null;
  estado_pago: 'PAGADO' | 'PARCIAL' | 'PENDIENTE';
  importe_total: number | null;
  percepcion: number | null;
  total_fise: number | null;
}

/** Totales del reporte de compras (tarjetas y pie de la tabla anterior). */
export interface ResumenCompras {
  compras: number;
  productos: number;
  lineas: number;
  monto_soles: number;
  monto_dolares: number;
  cantidad: number;
  basico: number;
  dscto: number;
  igv: number;
  total: number;
  validados: number;
  anulados: number;
}

/** Filtros del reporte de compras. */
export interface FiltrosCompras {
  fecha_inicio: string;
  fecha_fin: string;
  estado: string;
  proveedor_id: number | null;
  producto_id: number | null;
}

/** Respuesta del reporte de compras. */
export interface ComprasRespuesta {
  compras: LineaCompra[];
  resumen: ResumenCompras;
  filtros: {
    inicio: string;
    fin: string;
    estado: string | null;
    proveedorId: number | null;
    productoId: number | null;
  };
  generado_at: string;
}

/** Catálogos del reporte de compras. */
export interface CatalogosCompras {
  proveedores: { id: number; nombre: string; ruc: string | null }[];
  productos: { id: number; nombre: string; codigo: string | null }[];
  meses: { periodo: string; compras: number; total: number }[];
  rango: { desde: string | null; hasta: string | null; meses: number };
  estados: { valor: string; etiqueta: string }[];
  generado_at: string;
}

/** Fila del reporte de series globales del mes (una serie y un producto). */
export interface FilaSerie {
  tipo: string;
  estado: string;
  serie: string;
  producto_id: number | string;
  producto: string;
  cantidad: number | null;
  total: number | null;
}

/** Productos por tipo del reporte de series. */
export interface ProductosDeSeries {
  COMBUSTIBLE: { id: number; nombre: string }[];
  EXTRAS: { id: number; nombre: string }[];
}

/** Respuesta del reporte de series globales del mes. */
export interface SeriesGlobalesRespuesta {
  periodo: string;
  descripcion: string;
  filas: FilaSerie[];
  productos: ProductosDeSeries;
  tipos: { valor: string; etiqueta: string }[];
  estados: string[];
  generado_at: string;
}

/** Catálogos del reporte de series globales del mes. */
export interface CatalogosSeriesGlobales {
  tipos: { valor: string; etiqueta: string }[];
  estados: string[];
  productos: ProductosDeSeries;
  meses: { periodo: string; documentos: number }[];
  rango: { desde: string | null; hasta: string | null; meses: number };
  generado_at: string;
}

/** Filtros del reporte de series globales del mes. */
export interface FiltrosSeriesGlobales {
  periodo: string;
  tipo: string;
  estado: string;
}

/** Bóveda (ingreso a caja fuerte) de un turno. */
export interface BovedaDelTurno {
  id: number;
  turno_id: number;
  fecha: string | null;
  total: string | number;
  observacion: string | null;
  estado: number;
}

/** Gasto de un turno. */
export interface GastoDelTurno {
  id: number;
  turno_id: number;
  cantidad: string | number;
  descripcion: string | null;
  tipo_gasto: string | null;
  fecha_gasto: string | null;
  estado: number;
}

/** Venta de GNV registrada en un turno. */
export interface GnvDelTurno {
  id: number;
  turno_id: number;
  monto_gnv: string | number;
  volumen_m3: string | number;
  estado: number;
}

/** Configuración de la liquidación (el «monto mínimo faltante» de la empresa). */
export interface ConfiguracionLiquidacion {
  monto_minimo_faltante: number;
  tolerancia_diferencia: number;
  requiere_verificacion: boolean;
  permitir_modificacion_verificado: boolean;
  notificar_diferencias: boolean;
  dias_retencion_datos: number;
  categorias_turnos: { id: number; nombre: string; inicio: string; fin: string }[];
}

/** Transacción del controlador (PTS) guardada en el turno. */
export interface TransaccionTurno {
  idTransaction: number | null;
  keyTransaction: number | null;
  pumpTransaction: number | null;
  nozzleTransaction: number | null;
  FuelGradeName: string | null;
  serieTransaction: string | null;
  amountTransaction: string | number | null;
  volumeTransaction: string | number | null;
  priceTransaction: string | number | null;
  discountTransaction: string | number | null;
  voidedTransaction: number | null;
  dateTimeTransaction: string | null;
  /** Comprobante emitido para la transacción (el número que va tras la serie). */
  invoicedTransaction?: number | null;
  /** Contómetro de volumen al terminar la transacción. */
  totalVolumeTransaction?: string | number | null;
  /** Contómetro de importe al terminar la transacción. */
  totalAmountTransaction?: string | number | null;
  /** Descuento por unidad de volumen (el `total` va en `discountTransaction`). */
  priceDiscountTransaction?: string | number | null;
  /** La transacción consume un pago adelantado. */
  isAdvancePayment?: boolean | null;
  /** Número de la calibración, cuando la transacción es una. */
  calibrationTransaction?: number | null;
  /** Otras formas de la fecha que puede mandar el controlador. */
  transactionDate?: string | null;
  dateTransaction?: string | null;
}

/** Totales por serie del turno (primer carácter de la serie). */
export interface SerieDelTurno {
  codigo: string;
  nombre: string;
  cantidad_transacciones: number;
  total_monto: number;
  total_volumen: number;
}

/** Cuadre del turno: lo que debía quedar en caja y lo que falta o sobra. */
export interface ResumenFinancieroTurno {
  total_ventas_bruto: number;
  total_lubricantes: number;
  total_bovedas: number;
  total_gastos: number;
  total_ingresos_adicionales: number;
  total_descuentos: number;
  total_descuentos_solo_positivos: number;
  total_sobrecargos: number;
  total_descuentos_creditos: number;
  total_descuentos_sin_creditos: number;
  total_no_efectivo: number;
  metodos_pago_no_efectivo: { metodo: string; monto: number }[];
  efectivo_calculado: number;
  total_entregado: number;
  efectivo_pendiente: number;
  total_consumo_interno: number;
  total_creditos: number;
  total_canjes_cardplus: number;
  total_calibracion_galones: number;
  total_adelantados: number;
  porcentajes: {
    bovedas: number;
    gastos: number;
    descuentos: number;
    no_efectivo: number;
    lubricantes: number;
    efectivo: number;
  };
  desglose_series: {
    codigo: string;
    nombre: string;
    cantidad: number;
    monto: number;
    volumen: number;
    porcentaje: number;
  }[];
  desglose_descuentos: {
    tipo: string;
    nombre: string;
    total_descuento: number;
    total_sobrecargo: number;
    neto: number;
    cantidad: number;
  }[];
  estadisticas: {
    total_transacciones: number;
    volumen_total: number;
    precio_promedio: number;
    cantidad_bovedas: number;
    cantidad_gastos: number;
    total_lubricantes_cantidad: number;
    productos_lubricantes_diferentes: number;
    lineas_venta_lubricantes: number;
  };
}

/** Turno del día con su liquidación. */
export interface TurnoLiquidacion {
  id: number;
  estado: number | null;
  hora_llegada: string;
  hora_salida: string | null;
  usuario_id: number;
  first_name: string | null;
  last_name: string | null;
  estado_verificacion: number | null;
  fecha_verificacion: string | null;
  usuario_verificacion: number | null;
  tiene_gnv: string | null;
  volumen_gnv: number | string | null;
  monto_gnv: number | string | null;
  total_no_efectivo: number | string | null;
  total_advance_payments: number;
  advance_payments_count: number;
  boveda: { total: number; cantidad: number };
  gastos: { total: number; cantidad: number };
  lubricantes: { total_monto_lubricantes: number; total_cantidad_vendida: number };
  ingresos_adicionales: { total: number; cantidad: number };
  transacciones_series: {
    series: SerieDelTurno[];
    total_general_monto: number;
    total_general_volumen: number;
    total_transacciones: number;
  };
  descuentos: {
    desglose: ResumenFinancieroTurno['desglose_descuentos'];
    total_general_descuento: number;
    total_general_sobrecargo: number;
    total_general_neto: number;
  };
  resumen_financiero: ResumenFinancieroTurno;
  tipo_turno: { id: number | null; nombre: string; inicio: string | null; fin: string | null } | null;
  transactions: TransaccionTurno[];
}

/**
 * Turno con el cuadre **opcional**.
 *
 * El operador lo recibe así cuando la empresa trabaja con **cierre a ciegas**
 * (`config.closure = 'blind_closure'`, el defecto): sin `resumen_financiero` y con las
 * cifras de los contómetros en `null`, para que no pueda calcular la venta antes de
 * entregar el efectivo. El reporte de turnos siempre lo recibe completo.
 */
export type TurnoConCuadreOpcional = Omit<TurnoLiquidacion, 'resumen_financiero'> & {
  resumen_financiero: ResumenFinancieroTurno | null;
};

/** Estadísticas del día (panel anterior). */
export interface EstadisticasLiquidacion {
  total_turnos: number;
  turnos_verificados: number;
  turnos_pendientes: number;
  total_ventas: number;
  total_boveda: number;
  total_gastos: number;
  total_efectivo: number;
  total_tarjeta: number;
  total_yape: number;
  total_plin: number;
  total_transferencia: number;
  total_credito: number;
  total_gnv_monto: number;
  total_gnv_volumen: number;
  trabajadores: string[];
  total_trabajadores: number;
}

/** Permisos del usuario en la liquidación. */
export interface PermisosLiquidacion {
  verificar: boolean;
  verificar_masivo: boolean;
  deshacer_verificacion: boolean;
  deshacer_masivo: boolean;
  bovedas_crear: boolean;
  bovedas_editar: boolean;
  bovedas_eliminar: boolean;
  gastos_crear: boolean;
  gastos_editar: boolean;
  gastos_eliminar: boolean;
  metodos_pago_editar: boolean;
  metodos_pago_eliminar: boolean;
}

/** Cobros de un turno agrupados por método de pago (`liquidaciones_metodos_pago`). */
export interface MetodoPagoDelTurno {
  metodo_pago: string;
  tipo: string;
  total_monto: number;
  total_transacciones: number;
  promedio_por_transaccion: number;
}

/** Una venta cobrada con un método de pago del turno. */
export interface CobroDelTurno {
  venta_id: number;
  monto: number;
  total_venta: number;
  metodo_pago_primario: string;
  monto_pago_primario: number;
  metodo_pago_secundario: string;
  monto_pago_secundario: number;
  tipo_pago: 'PRIMARIO' | 'SECUNDARIO';
  referencia: string;
  referencia_pago_primario: string;
  referencia_pago_secundario: string;
  estado_api: string;
  cliente: string;
  placa: string;
  serie_comprobante: string;
  fecha: string;
}

/** Detalle de un método de pago del turno. */
export interface DetalleMetodoPago {
  metodo_pago: string;
  tipo_metodo: string;
  total_monto: number;
  total_transacciones: number;
  promedio_por_transaccion: number;
  /** Con el turno verificado (o rechazado) la corrección queda bloqueada. */
  turno_verificado: boolean;
  ventas: CobroDelTurno[];
}

/** Métodos de pago activos disponibles para corregir un cobro. */
export interface CatalogosMetodosPago {
  metodos: { id: number; nombre: string; tipo: string }[];
}

/** Filtros del reporte de ventas de GNV. */
export interface FiltrosVentasGnv {
  desde: string;
  hasta: string;
}

/** Un turno con venta de GNV registrada. */
export interface FilaGnv {
  turno_id: number;
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
  trabajador: string;
  volumen_m3: number;
  galones: number;
  monto: number;
  precio_m3: number;
}

/** Resumen del periodo del reporte de GNV. */
export interface ResumenGnv {
  total_turnos: number;
  total_volumen_m3: number;
  total_galones: number;
  total_monto: number;
  dias_activos: number;
  trabajadores: number;
  precio_m3: number;
}

/** Respuesta del reporte de ventas de GNV. */
export interface VentasGnvRespuesta {
  filas: FilaGnv[];
  resumen: ResumenGnv;
  rango: { inicio: string; fin: string };
  generado_at: string;
}

/** Métodos de pago que ofrece el reporte de ventas por método. */
export type MetodoDeVenta = 'VISA' | 'IZIPAY' | 'TRANSFERENCIA';

/** Filtros del reporte de ventas por método de pago. */
export interface FiltrosVentasPorMetodo {
  metodo: MetodoDeVenta;
  desde: string;
  hasta: string;
  /**
   * Vista del reporte: el detalle de documentos o el reparto por día
   * (`dia`). El JSON trae las dos cosas; la vista sólo decide qué pinta la
   * pantalla y qué descargan el CSV y el PDF.
   */
  vista?: 'documentos' | 'dia';
}

/** Un documento cobrado con el método del reporte. */
export interface FilaVentaMetodo {
  numero_comprobante: string;
  fecha: string;
  /** Día del cobro (`AAAA-MM-DD`), el que agrupa la vista por día. */
  dia: string | null;
  cliente: string;
  documento: string;
  monto: number;
  referencia: string;
  banco: string;
  turno_id: number | null;
  usuario: string;
  tipo_comprobante: string;
  origen: string;
  metodo_pago: string;
}

/** Resumen del periodo del reporte de ventas por método. */
export interface ResumenVentaMetodo {
  metodo_pago: string;
  total_documentos: number;
  total_monto: number;
  promedio: number;
  punto_de_venta: number;
  app: number;
}

/** Reparto por día del reporte de ventas por método. */
export interface DiaVentaMetodo {
  fecha: string;
  documentos: number;
  punto_de_venta: number;
  app: number;
  total: number;
}

/** Respuesta del reporte de ventas por método de pago. */
export interface VentasPorMetodoRespuesta {
  filas: FilaVentaMetodo[];
  resumen: ResumenVentaMetodo;
  por_dia: DiaVentaMetodo[];
  metodos: string[];
  rango: { inicio: string; fin: string };
  generado_at: string;
}

/** Cadena de consulta de un objeto de filtros. */
function cadenaDe(filtros: object): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === null || valor === '') continue;

    if (Array.isArray(valor)) {
      for (const item of valor) parametros.append(clave, String(item));
      continue;
    }

    parametros.set(clave, String(valor));
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const reportesApi = {
  indice: (signal?: AbortSignal): Promise<{ reportes: ReporteDelIndice[]; generado_at: string }> =>
    apiClient.get<{ reportes: ReporteDelIndice[]; generado_at: string }>('/api/reportes', { signal }),

  catalogosHistorial: (signal?: AbortSignal): Promise<CatalogosHistorial> =>
    apiClient.get<CatalogosHistorial>('/api/reportes/historial-precios/catalogos', { signal }),

  historialPrecios: (
    filtros: FiltrosHistorial,
    signal?: AbortSignal,
  ): Promise<HistorialPreciosRespuesta> =>
    apiClient.get<HistorialPreciosRespuesta>(
      `/api/reportes/historial-precios${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte. */
  exportarHistorialPrecios: (filtros: FiltrosHistorial): string =>
    `/reportes/historial-precios/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte (mismo listado). */
  pdfHistorialPrecios: (filtros: FiltrosHistorial): string =>
    `/reportes/historial-precios/pdf${cadenaDe(filtros)}`,

  catalogosDocumentos: (signal?: AbortSignal): Promise<CatalogosDocumentos> =>
    apiClient.get<CatalogosDocumentos>('/api/reportes/documentos/catalogos', { signal }),

  documentos: (filtros: FiltrosDocumentos, signal?: AbortSignal): Promise<DocumentosRespuesta> =>
    apiClient.get<DocumentosRespuesta>(`/api/reportes/documentos${cadenaDe(filtros)}`, { signal }),

  /** Ruta de la exportación a Excel (CSV) del listado de documentos. */
  exportarDocumentos: (filtros: FiltrosDocumentos): string =>
    `/reportes/documentos/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del listado de documentos. */
  pdfDocumentos: (filtros: FiltrosDocumentos): string =>
    `/reportes/documentos/pdf${cadenaDe(filtros)}`,

  catalogosVentaProductos: (signal?: AbortSignal): Promise<CatalogosVentaProductos> =>
    apiClient.get<CatalogosVentaProductos>('/api/reportes/venta-productos/catalogos', { signal }),

  ventaProductos: (
    filtros: FiltrosVentaProductos,
    signal?: AbortSignal,
  ): Promise<VentaProductosRespuesta> =>
    apiClient.get<VentaProductosRespuesta>(
      `/api/reportes/venta-productos${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte de ventas de productos. */
  exportarVentaProductos: (filtros: FiltrosVentaProductos): string =>
    `/reportes/venta-productos/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de ventas de productos. */
  pdfVentaProductos: (filtros: FiltrosVentaProductos): string =>
    `/reportes/venta-productos/pdf${cadenaDe(filtros)}`,

  catalogosVentasDescuentos: (
    rango: { desde: string; hasta: string },
    signal?: AbortSignal,
  ): Promise<CatalogosVentasDescuentos> =>    apiClient.get<CatalogosVentasDescuentos>(
      `/api/reportes/ventas-descuentos/catalogos${cadenaDe(rango)}`,
      { signal },
    ),

  ventasDescuentos: (
    filtros: FiltrosVentasDescuentos,
    signal?: AbortSignal,
  ): Promise<VentasDescuentosRespuesta> =>
    apiClient.get<VentasDescuentosRespuesta>(
      `/api/reportes/ventas-descuentos${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte de ventas y descuentos. */
  exportarVentasDescuentos: (filtros: FiltrosVentasDescuentos): string =>
    `/reportes/ventas-descuentos/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de ventas y descuentos. */
  pdfVentasDescuentos: (filtros: FiltrosVentasDescuentos): string =>
    `/reportes/ventas-descuentos/pdf${cadenaDe(filtros)}`,

  catalogosFacturasPendientes: (
    rango: { desde: string; hasta: string },
    signal?: AbortSignal,
  ): Promise<CatalogosFacturasPendientes> =>
    apiClient.get<CatalogosFacturasPendientes>(
      `/api/reportes/facturas-pendientes/catalogos${cadenaDe(rango)}`,
      { signal },
    ),

  facturasPendientes: (
    filtros: FiltrosFacturasPendientes,
    signal?: AbortSignal,
  ): Promise<FacturasPendientesRespuesta> =>
    apiClient.get<FacturasPendientesRespuesta>(
      `/api/reportes/facturas-pendientes${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte de facturas pendientes. */
  exportarFacturasPendientes: (filtros: FiltrosFacturasPendientes): string =>
    `/reportes/facturas-pendientes/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de facturas pendientes. */
  pdfFacturasPendientes: (filtros: FiltrosFacturasPendientes): string =>
    `/reportes/facturas-pendientes/pdf${cadenaDe(filtros)}`,

  catalogosSerieFechaProducto: (signal?: AbortSignal): Promise<CatalogosSerieFechaProducto> =>
    apiClient.get<CatalogosSerieFechaProducto>('/api/reportes/serie-fecha-producto/catalogos', {
      signal,
    }),

  serieFechaProducto: (
    filtros: FiltrosSerieFechaProducto,
    signal?: AbortSignal,
  ): Promise<SerieFechaProductoRespuesta> =>
    apiClient.get<SerieFechaProductoRespuesta>(
      `/api/reportes/serie-fecha-producto${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte por serie. */
  exportarSerieFechaProducto: (filtros: FiltrosSerieFechaProducto): string =>
    `/reportes/serie-fecha-producto/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte por serie. */
  pdfSerieFechaProducto: (filtros: FiltrosSerieFechaProducto): string =>
    `/reportes/serie-fecha-producto/pdf${cadenaDe(filtros)}`,

  catalogosKardex: (signal?: AbortSignal): Promise<CatalogosKardex> =>
    apiClient.get<CatalogosKardex>('/api/reportes/kardex/catalogos', { signal }),

  kardex: (filtros: FiltrosKardex, signal?: AbortSignal): Promise<KardexRespuesta> =>
    apiClient.get<KardexRespuesta>(`/api/reportes/kardex${cadenaDe(filtros)}`, { signal }),

  /** Ruta de la exportación a Excel (CSV) del kardex. */
  exportarKardex: (filtros: FiltrosKardex): string =>
    `/reportes/kardex/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del kardex. */
  pdfKardex: (filtros: FiltrosKardex): string => `/reportes/kardex/pdf${cadenaDe(filtros)}`,

  catalogosCreditosCliente: (signal?: AbortSignal): Promise<CatalogosCreditosCliente> =>
    apiClient.get<CatalogosCreditosCliente>('/api/reportes/creditos-cliente/catalogos', { signal }),

  creditosCliente: (
    filtros: FiltrosCreditosCliente,
    signal?: AbortSignal,
  ): Promise<CreditosClienteRespuesta> =>
    apiClient.get<CreditosClienteRespuesta>(
      `/api/reportes/creditos-cliente${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte de créditos por cliente. */
  exportarCreditosCliente: (filtros: FiltrosCreditosCliente): string =>
    `/reportes/creditos-cliente/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de créditos por cliente. */
  pdfCreditosCliente: (filtros: FiltrosCreditosCliente): string =>
    `/reportes/creditos-cliente/pdf${cadenaDe(filtros)}`,

  catalogosFaltantesPersonal: (signal?: AbortSignal): Promise<CatalogosFaltantesPersonal> =>
    apiClient.get<CatalogosFaltantesPersonal>('/api/reportes/faltantes-personal/catalogos', {
      signal,
    }),

  faltantesPersonal: (
    filtros: FiltrosFaltantesPersonal,
    signal?: AbortSignal,
  ): Promise<FaltantesPersonalRespuesta> =>
    apiClient.get<FaltantesPersonalRespuesta>(
      `/api/reportes/faltantes-personal${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte de faltantes. */
  exportarFaltantesPersonal: (filtros: FiltrosFaltantesPersonal): string =>
    `/reportes/faltantes-personal/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de faltantes. */
  pdfFaltantesPersonal: (filtros: FiltrosFaltantesPersonal): string =>
    `/reportes/faltantes-personal/pdf${cadenaDe(filtros)}`,

  catalogosDescuentosActivos: (signal?: AbortSignal): Promise<CatalogosDescuentosActivos> =>
    apiClient.get<CatalogosDescuentosActivos>('/api/reportes/descuentos-activos/catalogos', {
      signal,
    }),

  descuentosActivos: (
    filtros: FiltrosDescuentosActivos,
    signal?: AbortSignal,
  ): Promise<DescuentosActivosRespuesta> =>
    apiClient.get<DescuentosActivosRespuesta>(
      `/api/reportes/descuentos-activos${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte de descuentos activos. */
  exportarDescuentosActivos: (filtros: FiltrosDescuentosActivos): string =>
    `/reportes/descuentos-activos/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de descuentos activos. */
  pdfDescuentosActivos: (filtros: FiltrosDescuentosActivos): string =>
    `/reportes/descuentos-activos/pdf${cadenaDe(filtros)}`,

  catalogosProductividad: (signal?: AbortSignal): Promise<CatalogosProductividad> =>
    apiClient.get<CatalogosProductividad>('/api/reportes/productividad-turnos/catalogos', {
      signal,
    }),

  productividadTurnos: (
    filtros: FiltrosProductividad,
    signal?: AbortSignal,
  ): Promise<ProductividadRespuesta> =>
    apiClient.get<ProductividadRespuesta>(
      `/api/reportes/productividad-turnos${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte de productividad. */
  exportarProductividad: (filtros: FiltrosProductividad): string =>
    `/reportes/productividad-turnos/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de productividad. */
  pdfProductividad: (filtros: FiltrosProductividad): string =>
    `/reportes/productividad-turnos/pdf${cadenaDe(filtros)}`,

  catalogosMovimientosCuenta: (signal?: AbortSignal): Promise<CatalogosMovimientosCuenta> =>
    apiClient.get<CatalogosMovimientosCuenta>('/api/reportes/movimientos-cuenta/catalogos', {
      signal,
    }),

  movimientosCuenta: (
    filtros: FiltrosMovimientosCuenta,
    signal?: AbortSignal,
  ): Promise<MovimientosCuentaRespuesta> =>
    apiClient.get<MovimientosCuentaRespuesta>(
      `/api/reportes/movimientos-cuenta${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) de los movimientos en cuenta. */
  exportarMovimientosCuenta: (filtros: FiltrosMovimientosCuenta): string =>
    `/reportes/movimientos-cuenta/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF de los movimientos en cuenta. */
  pdfMovimientosCuenta: (filtros: FiltrosMovimientosCuenta): string =>
    `/reportes/movimientos-cuenta/pdf${cadenaDe(filtros)}`,

  nubecont: (filtros: FiltrosNubecont, signal?: AbortSignal): Promise<NubecontRespuesta> =>
    apiClient.get<NubecontRespuesta>(`/api/reportes/nubecont${cadenaDe(filtros)}`, { signal }),

  /** Ruta de la exportación a Excel (CSV) del listado de Nubecont. */
  exportarNubecont: (filtros: FiltrosNubecont): string =>
    `/reportes/nubecont/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del listado de Nubecont. */
  pdfNubecont: (filtros: FiltrosNubecont): string =>
    `/reportes/nubecont/pdf${cadenaDe(filtros)}`,

  pagosAdelantados: (
    filtros: FiltrosPagosAdelantados,
    signal?: AbortSignal,
  ): Promise<PagosAdelantadosRespuesta> =>
    apiClient.get<PagosAdelantadosRespuesta>(
      `/api/pagos-adelantados-documentos${cadenaDe(filtros)}`,
      { signal },
    ),

  detallePagoAdelantado: (
    venta: number,
    signal?: AbortSignal,
  ): Promise<DetallePagoAdelantado> =>
    apiClient.get<DetallePagoAdelantado>(`/api/pagos-adelantados-documentos/${venta}/detalle`, {
      signal,
    }),

  /** Sube (o reemplaza) el documento de pago de la factura. */
  subirDocumentoPago: (venta: number, archivo: File): Promise<{ documento: string; mensaje: string }> => {
    const formulario = new FormData();
    formulario.append('documento', archivo);

    return apiClient.post<{ documento: string; mensaje: string }>(
      `/api/pagos-adelantados-documentos/${venta}/documento`,
      { body: formulario, timeout: 60_000 },
    );
  },

  /** Ruta del documento de pago (se abre con el token de la sesión). */
  urlDocumentoPago: (venta: number): string => `/api/pagos-adelantados-documentos/${venta}/documento`,

  eliminarDocumentoPago: (venta: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/pagos-adelantados-documentos/${venta}/documento`),

  catalogosBitacora: (signal?: AbortSignal): Promise<CatalogosBitacora> =>
    apiClient.get<CatalogosBitacora>('/api/reportes/bitacora-izipay/catalogos', { signal }),
  bitacoraIzipay: (
    filtros: FiltrosBitacora,
    signal?: AbortSignal,
  ): Promise<BitacoraRespuesta> =>
    apiClient.get<BitacoraRespuesta>(
      `/api/reportes/bitacora-izipay${cadenaDe(filtros)}`,
      { signal },
    ),

  detalleBitacora: (id: number, signal?: AbortSignal): Promise<{ registro: DetalleBitacora }> =>
    apiClient.get<{ registro: DetalleBitacora }>(
      `/api/reportes/bitacora-izipay/detalle${cadenaDe({ id })}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) de la bitácora. */
  exportarBitacora: (filtros: FiltrosBitacora): string =>
    `/reportes/bitacora-izipay/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF de la bitácora. */
  pdfBitacora: (filtros: FiltrosBitacora): string =>
    `/reportes/bitacora-izipay/pdf${cadenaDe(filtros)}`,

  catalogosPlacas: (signal?: AbortSignal): Promise<CatalogosPlacas> =>    apiClient.get<CatalogosPlacas>('/api/reportes/placas-clientes/catalogos', { signal }),

  placasClientes: (filtros: FiltrosPlacas, signal?: AbortSignal): Promise<PlacasRespuesta> =>
    apiClient.get<PlacasRespuesta>(`/api/reportes/placas-clientes${cadenaDe(filtros)}`, {
      signal,
    }),

  catalogosConsumoInterno: (signal?: AbortSignal): Promise<CatalogosConsumoInterno> =>
    apiClient.get<CatalogosConsumoInterno>('/api/reportes/consumo-interno/catalogos', { signal }),

  consumoInterno: (
    filtros: FiltrosConsumoInterno,
    signal?: AbortSignal,
  ): Promise<ConsumoInternoRespuesta> =>
    apiClient.get<ConsumoInternoRespuesta>(
      `/api/reportes/consumo-interno${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del consumo interno. */
  exportarConsumoInterno: (filtros: FiltrosConsumoInterno): string =>
    `/reportes/consumo-interno/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del consumo interno. */
  pdfConsumoInterno: (filtros: FiltrosConsumoInterno): string =>
    `/reportes/consumo-interno/pdf${cadenaDe(filtros)}`,

  catalogosGastos: (signal?: AbortSignal): Promise<CatalogosGastos> =>
    apiClient.get<CatalogosGastos>('/api/reportes/gastos/catalogos', { signal }),

  gastosPeriodo: (filtros: FiltrosGastos, signal?: AbortSignal): Promise<GastosRespuesta> =>
    apiClient.get<GastosRespuesta>(`/api/reportes/gastos${cadenaDe(filtros)}`, { signal }),

  /** Ruta de la exportación a Excel (CSV) del reporte de gastos. */
  exportarGastos: (filtros: FiltrosGastos): string =>
    `/reportes/gastos/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de gastos. */
  pdfGastos: (filtros: FiltrosGastos): string => `/reportes/gastos/pdf${cadenaDe(filtros)}`,

  catalogosIngresoBoveda: (signal?: AbortSignal): Promise<CatalogosIngresoBoveda> =>
    apiClient.get<CatalogosIngresoBoveda>('/api/reportes/ingreso-boveda/catalogos', { signal }),

  ingresoBoveda: (
    filtros: FiltrosIngresoBoveda,
    signal?: AbortSignal,
  ): Promise<IngresoBovedaRespuesta> =>
    apiClient.get<IngresoBovedaRespuesta>(`/api/reportes/ingreso-boveda${cadenaDe(filtros)}`, {
      signal,
    }),

  /** Ruta de la exportación a Excel (CSV) del reporte de bóveda. */
  exportarIngresoBoveda: (filtros: FiltrosIngresoBoveda): string =>
    `/reportes/ingreso-boveda/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de bóveda. */
  pdfIngresoBoveda: (filtros: FiltrosIngresoBoveda): string =>
    `/reportes/ingreso-boveda/pdf${cadenaDe(filtros)}`,

  catalogosCompras: (signal?: AbortSignal): Promise<CatalogosCompras> =>
    apiClient.get<CatalogosCompras>('/api/reportes/compra-productos/catalogos', { signal }),
  compras: (filtros: FiltrosCompras, signal?: AbortSignal): Promise<ComprasRespuesta> =>
    apiClient.get<ComprasRespuesta>(
      `/api/reportes/compra-productos${cadenaDe(filtros)}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte de compras. */
  exportarCompras: (filtros: FiltrosCompras): string =>
    `/reportes/compra-productos/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de compras. */
  pdfCompras: (filtros: FiltrosCompras): string =>
    `/reportes/compra-productos/pdf${cadenaDe(filtros)}`,

  catalogosSeriesGlobales: (signal?: AbortSignal): Promise<CatalogosSeriesGlobales> =>
    apiClient.get<CatalogosSeriesGlobales>('/api/reportes/series-globales-mes/catalogos', {
      signal,
    }),

  seriesGlobales: (
    filtros: FiltrosSeriesGlobales,
    signal?: AbortSignal,
  ): Promise<SeriesGlobalesRespuesta> =>
    apiClient.get<SeriesGlobalesRespuesta>(
      `/api/reportes/series-globales-mes${cadenaDe({ periodo: filtros.periodo })}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) del reporte de series. */
  exportarSeriesGlobales: (filtros: FiltrosSeriesGlobales): string =>
    `/reportes/series-globales-mes/exportar${cadenaDe(filtros)}`,

  /** Ruta del PDF del reporte de series. */
  pdfSeriesGlobales: (filtros: FiltrosSeriesGlobales): string =>
    `/reportes/series-globales-mes/pdf${cadenaDe(filtros)}`,

  turnosDelDia: (fecha: string, signal?: AbortSignal): Promise<TurnoLiquidacion[]> =>
    apiClient.get<TurnoLiquidacion[]>(`/api/liquidaciones_turnos${cadenaDe({ date: fecha })}`, {
      signal,
    }),

  estadisticasLiquidacion: (
    fecha: string,
    signal?: AbortSignal,
  ): Promise<EstadisticasLiquidacion> =>
    apiClient.get<EstadisticasLiquidacion>(
      `/api/liquidaciones_estadisticas${cadenaDe({ date: fecha })}`,
      { signal },
    ),

  permisosLiquidacion: (signal?: AbortSignal): Promise<PermisosLiquidacion> =>
    apiClient.get<PermisosLiquidacion>('/api/liquidaciones_permisos', { signal }),

  /** Verifica un turno indicando el efectivo que se recibió. */
  verificarTurno: (cuerpo: {
    turno_id: number;
    usuario_id: number;
    efectivo_recibido: number;
    efectivo_esperado: number;
    detalle: string;
  }): Promise<{ success: boolean; message: string }> =>
    apiClient.post<{ success: boolean; message: string }>('/api/liquidaciones_verificar_turno', {
      body: {
        turno_id: cuerpo.turno_id,
        datos_verificacion: [
          {
            usuario_id: cuerpo.usuario_id,
            estado_turno_verificar: 2,
            total_entregado: cuerpo.efectivo_recibido,
            real_monto_recibido_verificar: cuerpo.efectivo_recibido,
            total_entregar: cuerpo.efectivo_esperado,
            detalle_verificar: cuerpo.detalle,
          },
        ],
      },
    }),

  /** Rechaza un turno (estado 3) indicando el efectivo real y el detalle. */
  rechazarTurno: (cuerpo: {
    turno_id: number;
    usuario_id: number;
    efectivo_recibido: number;
    efectivo_esperado: number;
    detalle: string;
  }): Promise<{ success: boolean; message: string }> =>
    apiClient.post<{ success: boolean; message: string }>('/api/liquidaciones_verificar_turno', {
      body: {
        turno_id: cuerpo.turno_id,
        datos_verificacion: [
          {
            usuario_id: cuerpo.usuario_id,
            estado_turno_verificar: 3,
            total_entregado: 0,
            real_monto_recibido_verificar: cuerpo.efectivo_recibido,
            total_entregar: cuerpo.efectivo_esperado,
            detalle_verificar: cuerpo.detalle,
          },
        ],
      },
    }),

  deshacerVerificacion: (turnoId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.post<{ success: boolean; message: string }>(
      '/api/liquidaciones_deshacer_verificacion',
      { body: { turno_id: turnoId } },
    ),

  verificarMasivo: (fecha: string): Promise<{ message?: string }> =>
    apiClient.post<{ message?: string }>('/api/liquidaciones_verificar_masivo', {
      body: { date: fecha },
    }),

  deshacerMasivo: (fecha: string): Promise<{ message?: string }> =>
    apiClient.post<{ message?: string }>('/api/liquidaciones_deshacer_masivo', {
      body: { date: fecha },
    }),

  /** Ruta de la exportación a Excel (CSV) de los turnos del día. */
  exportarTurnos: (fecha: string): string =>
    `/liquidaciones_turnos_exportar${cadenaDe({ date: fecha })}`,

  /** Ruta de la exportación a Excel (CSV) de las transacciones (día o turno). */
  exportarTransacciones: (fecha: string, turnoId?: number): string =>
    `/liquidaciones_transacciones_exportar${cadenaDe({ date: fecha, turno_id: turnoId ?? null })}`,

  /** Ruta del ticket de cierre del turno en PDF (recibo de 80 mm). */
  ticketTurno: (turnoId: number): string =>
    `/liquidaciones_ticket_turno${cadenaDe({ turno_id: turnoId })}`,

  /** Ruta del parte diario del día en PDF (A4 horizontal). */
  parteDiario: (fecha: string): string => `/liquidaciones_parte_diario${cadenaDe({ date: fecha })}`,

  /** Ventas de GNV del periodo. */
  ventasGnv: (filtros: FiltrosVentasGnv, signal?: AbortSignal): Promise<VentasGnvRespuesta> =>
    apiClient.get<VentasGnvRespuesta>(`/api/reportes/ventas-gnv${cadenaDe({ ...filtros })}`, {
      signal,
    }),

  /** Ruta de la exportación a Excel (CSV) de las ventas de GNV. */
  exportarVentasGnv: (filtros: FiltrosVentasGnv): string =>
    `/reportes/ventas-gnv/exportar${cadenaDe({ ...filtros })}`,

  /** Ruta del PDF de las ventas de GNV. */
  pdfVentasGnv: (filtros: FiltrosVentasGnv): string =>
    `/reportes/ventas-gnv/pdf${cadenaDe({ ...filtros })}`,

  /** Ventas cobradas con un método de pago (VISA, IZIPAY, TRANSFERENCIA). */
  ventasPorMetodo: (
    filtros: FiltrosVentasPorMetodo,
    signal?: AbortSignal,
  ): Promise<VentasPorMetodoRespuesta> =>
    apiClient.get<VentasPorMetodoRespuesta>(
      `/api/reportes/ventas-por-metodo${cadenaDe({ ...filtros })}`,
      { signal },
    ),

  /** Ruta de la exportación a Excel (CSV) de las ventas por método. */
  exportarVentasPorMetodo: (filtros: FiltrosVentasPorMetodo): string =>
    `/reportes/ventas-por-metodo/exportar${cadenaDe({ ...filtros })}`,

  /** Ruta del PDF de las ventas por método. */
  pdfVentasPorMetodo: (filtros: FiltrosVentasPorMetodo): string =>
    `/reportes/ventas-por-metodo/pdf${cadenaDe({ ...filtros })}`,

  /** Cobros del turno agrupados por método de pago. */
  metodosPagoDelTurno: (turnoId: number, signal?: AbortSignal): Promise<MetodoPagoDelTurno[]> =>
    apiClient.get<MetodoPagoDelTurno[]>(
      `/api/liquidaciones_metodos_pago${cadenaDe({ turno_id: turnoId })}`,
      { signal },
    ),

  /** Ventas cobradas con un método de pago del turno. */
  detallesMetodoPago: (
    turnoId: number,
    metodoPago: string,
    signal?: AbortSignal,
  ): Promise<DetalleMetodoPago> =>
    apiClient.get<DetalleMetodoPago>(
      `/api/liquidaciones_metodos_pago_detalles${cadenaDe({
        turno_id: turnoId,
        metodo_pago: metodoPago,
      })}`,
      { signal },
    ),

  /** Métodos de pago activos, para corregir el cobro de una venta. */
  catalogosMetodosPago: (signal?: AbortSignal): Promise<CatalogosMetodosPago> =>
    apiClient.get<CatalogosMetodosPago>('/api/liquidaciones_metodos_pago_disponibles', { signal }),

  /** Corrige una parte del pago y su importe, conservando el total de la venta. */
  actualizarMetodoPago: (cuerpo: {
    venta_id: number;
    tipo_pago: 'PRIMARIO' | 'SECUNDARIO';
    nuevo_metodo_pago: string;
    monto: number;
    referencia?: string;
  }): Promise<{ success: boolean; message: string; metodo_pago: string }> =>
    apiClient.post<{ success: boolean; message: string; metodo_pago: string }>(
      '/api/liquidaciones_actualizar_metodo_pago',
      { body: cuerpo },
    ),

  configuracionLiquidacion: (signal?: AbortSignal): Promise<ConfiguracionLiquidacion> =>
    apiClient.get<ConfiguracionLiquidacion>('/api/liquidaciones_configuracion', { signal }),

  guardarConfiguracionLiquidacion: (montoMinimo: number): Promise<{ success: boolean }> =>
    apiClient.post<{ success: boolean }>('/api/liquidaciones_configuracion', {
      body: { monto_minimo_faltante: montoMinimo },
    }),

  bovedasDelTurno: (turnoId: number, signal?: AbortSignal): Promise<BovedaDelTurno[]> =>
    apiClient.get<BovedaDelTurno[]>(
      `/api/liquidaciones_bovedas${cadenaDe({ turno_id: turnoId })}`,
      { signal },
    ),

  crearBoveda: (cuerpo: {
    turno_id: number;
    total: number;
    observacion: string;
    /** Clave del envío: un reintento con la misma clave no registra el dinero dos veces. */
    clave_idempotencia?: string;
  }) =>
    apiClient.post<{ boveda_id: number }>('/api/liquidaciones_bovedas', { body: cuerpo }),

  editarBoveda: (id: number, cuerpo: { total: number; observacion: string }) =>
    apiClient.put<{ boveda_id: number }>(`/api/liquidaciones_bovedas/${id}`, { body: cuerpo }),

  inhabilitarBoveda: (id: number) => apiClient.delete<{ boveda_id: number }>(`/api/liquidaciones_bovedas/${id}`),

  gastosDelTurno: (turnoId: number, signal?: AbortSignal): Promise<GastoDelTurno[]> =>
    apiClient.get<GastoDelTurno[]>(`/api/liquidaciones_gastos${cadenaDe({ turno_id: turnoId })}`, {
      signal,
    }),

  crearGasto: (cuerpo: {
    turno_id: number;
    cantidad: number;
    descripcion: string;
    tipo: string;
    /** Clave del envío: un reintento con la misma clave no registra el gasto dos veces. */
    clave_idempotencia?: string;
  }) => apiClient.post<{ gasto_id: number }>('/api/liquidaciones_gastos', { body: cuerpo }),

  editarGasto: (
    id: number,
    cuerpo: { cantidad: number; descripcion: string; tipo: string },
  ) => apiClient.put<{ gasto_id: number }>(`/api/liquidaciones_gastos/${id}`, { body: cuerpo }),

  inhabilitarGasto: (id: number) => apiClient.delete<{ gasto_id: number }>(`/api/liquidaciones_gastos/${id}`),

  gnvDelTurno: (turnoId: number, signal?: AbortSignal): Promise<GnvDelTurno[]> =>
    apiClient.get<GnvDelTurno[]>(`/api/liquidaciones_gnv${cadenaDe({ turno_id: turnoId })}`, {
      signal,
    }),

  guardarGnv: (cuerpo: { turno_id: number; monto_gnv: number; volumen_m3: number }) =>
    apiClient.post<{ id: number }>('/api/liquidaciones_gnv', { body: cuerpo }),

  eliminarGnv: (id: number) => apiClient.delete<{ id: number }>(`/api/liquidaciones_gnv/${id}`),
};

/** Índice de reportes. */
export function useIndiceReportes() {
  return useQuery({
    queryKey: [CLAVE, 'indice'],
    queryFn: ({ signal }) => reportesApi.indice(signal),
    staleTime: 300_000,
  });
}

/** Catálogos del historial de precios. */
export function useCatalogosHistorial() {
  return useQuery({
    queryKey: [CLAVE, 'historial-precios', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosHistorial(signal),
    staleTime: 300_000,
  });
}

/** Historial de cambios de precios. */
export function useHistorialPrecios(filtros: FiltrosHistorial) {
  return useQuery({
    queryKey: [CLAVE, 'historial-precios', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.historialPrecios(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Catálogos del listado de documentos (series y meses con datos). */
export function useCatalogosDocumentos() {
  return useQuery({
    queryKey: [CLAVE, 'documentos', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosDocumentos(signal),
    staleTime: 300_000,
  });
}

/** Listado de documentos de un mes. */
export function useDocumentos(filtros: FiltrosDocumentos) {
  return useQuery({
    queryKey: [CLAVE, 'documentos', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.documentos(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Catálogos del reporte de ventas de productos (productos, series y meses). */
export function useCatalogosVentaProductos() {
  return useQuery({
    queryKey: [CLAVE, 'venta-productos', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosVentaProductos(signal),
    staleTime: 300_000,
  });
}

/** Reporte de ventas de productos de un mes. */
export function useVentaProductos(filtros: FiltrosVentaProductos) {
  return useQuery({
    queryKey: [CLAVE, 'venta-productos', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.ventaProductos(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Catálogos del reporte de gastos (trabajadores, tipos y meses con gastos). */
export function useCatalogosGastos() {
  return useQuery({
    queryKey: [CLAVE, 'gastos', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosGastos(signal),
    staleTime: 300_000,
  });
}

/** Reporte de gastos de un rango de fechas. */
export function useGastosPeriodo(filtros: FiltrosGastos) {
  return useQuery({
    queryKey: [CLAVE, 'gastos', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.gastosPeriodo(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Catálogos del reporte de ingreso a bóveda (trabajadores y meses). */
export function useCatalogosIngresoBoveda() {
  return useQuery({
    queryKey: [CLAVE, 'ingreso-boveda', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosIngresoBoveda(signal),
    staleTime: 300_000,
  });
}

/** Reporte de ingreso a bóveda de un rango de fechas. */
export function useIngresoBoveda(filtros: FiltrosIngresoBoveda) {
  return useQuery({
    queryKey: [CLAVE, 'ingreso-boveda', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.ingresoBoveda(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Catálogos del buscador de placas (orígenes y tipos). */
export function useCatalogosPlacas() {
  return useQuery({
    queryKey: [CLAVE, 'placas-clientes', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosPlacas(signal),
    staleTime: 300_000,
  });
}

/** Catálogos del consumo interno (productos y meses). */
export function useCatalogosConsumoInterno() {
  return useQuery({
    queryKey: [CLAVE, 'consumo-interno', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosConsumoInterno(signal),
    staleTime: 300_000,
  });
}

/** Consumo interno ejecutivo del rango. */
export function useConsumoInterno(filtros: FiltrosConsumoInterno) {
  return useQuery({
    queryKey: [CLAVE, 'consumo-interno', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.consumoInterno(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Placas registradas con su cliente. */
export function usePlacasClientes(filtros: FiltrosPlacas) {
  return useQuery({
    queryKey: [CLAVE, 'placas-clientes', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.placasClientes(filtros, signal),
    staleTime: 300_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Catálogos de la bitácora IZIPAY (operaciones, niveles y meses). */
export function useCatalogosBitacora() {
  return useQuery({
    queryKey: [CLAVE, 'bitacora-izipay', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosBitacora(signal),
    staleTime: 300_000,
  });
}

/** Bitácora IZIPAY del rango elegido. */
export function useBitacoraIzipay(filtros: FiltrosBitacora) {
  return useQuery({
    queryKey: [CLAVE, 'bitacora-izipay', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.bitacoraIzipay(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Detalle de un registro de la bitácora (con sus JSON). */
export function useDetalleBitacora(id: number | null) {
  return useQuery({
    queryKey: [CLAVE, 'bitacora-izipay', 'detalle', id],
    queryFn: ({ signal }) => reportesApi.detalleBitacora(id ?? 0, signal),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

/** Catálogos del reporte de ventas y descuentos (clientes del rango y filtros). */
export function useCatalogosVentasDescuentos(rango: { desde: string; hasta: string }) {
  return useQuery({
    queryKey: [CLAVE, 'ventas-descuentos', 'catalogos', rango],
    queryFn: ({ signal }) => reportesApi.catalogosVentasDescuentos(rango, signal),
    staleTime: 300_000,
  });
}

/** Reporte de ventas y descuentos por comprobante y cliente. */
export function useVentasDescuentos(filtros: FiltrosVentasDescuentos) {
  return useQuery({
    queryKey: [CLAVE, 'ventas-descuentos', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.ventasDescuentos(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Catálogos del reporte de compras (proveedores, productos y meses). */
export function useCatalogosCompras() {
  return useQuery({
    queryKey: [CLAVE, 'compra-productos', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosCompras(signal),
    staleTime: 300_000,
  });
}

/** Reporte de compras por producto. */
export function useCompras(filtros: FiltrosCompras) {
  return useQuery({
    queryKey: [CLAVE, 'compra-productos', 'listado', filtros],
    queryFn: ({ signal }) => reportesApi.compras(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Catálogos del reporte de series globales del mes. */
export function useCatalogosSeriesGlobales() {
  return useQuery({
    queryKey: [CLAVE, 'series-globales-mes', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosSeriesGlobales(signal),
    staleTime: 300_000,
  });
}

/** Reporte de series globales del mes (todo el mes en una sola consulta). */
export function useSeriesGlobales(filtros: FiltrosSeriesGlobales) {
  return useQuery({
    queryKey: [CLAVE, 'series-globales-mes', 'listado', filtros.periodo],
    queryFn: ({ signal }) => reportesApi.seriesGlobales(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Turnos del día con su liquidación. */
export function useTurnosDelDia(fecha: string) {
  return useQuery({
    queryKey: [CLAVE, 'turnos', 'dia', fecha],
    queryFn: ({ signal }) => reportesApi.turnosDelDia(fecha, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Estadísticas de la liquidación del día. */
export function useEstadisticasLiquidacion(fecha: string) {
  return useQuery({
    queryKey: [CLAVE, 'turnos', 'estadisticas', fecha],
    queryFn: ({ signal }) => reportesApi.estadisticasLiquidacion(fecha, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Permisos del usuario en la liquidación (los botones que puede usar). */
export function usePermisosLiquidacion() {
  return useQuery({
    queryKey: [CLAVE, 'turnos', 'permisos'],
    queryFn: ({ signal }) => reportesApi.permisosLiquidacion(signal),
    staleTime: 300_000,
  });
}

/** Cobros del turno agrupados por método de pago. */
export function useMetodosPagoDelTurno(turnoId: number | null) {
  return useQuery({
    queryKey: [CLAVE, 'turnos', 'metodos-pago', turnoId],
    queryFn: ({ signal }) => reportesApi.metodosPagoDelTurno(Number(turnoId), signal),
    enabled: Boolean(turnoId),
    staleTime: 15_000,
  });
}

/** Ventas cobradas con un método de pago del turno. */
export function useDetallesMetodoPago(turnoId: number | null, metodoPago: string | null) {
  return useQuery({
    queryKey: [CLAVE, 'turnos', 'metodos-pago-detalle', turnoId, metodoPago],
    queryFn: ({ signal }) => reportesApi.detallesMetodoPago(Number(turnoId), String(metodoPago), signal),
    enabled: Boolean(turnoId) && Boolean(metodoPago),
    staleTime: 15_000,
  });
}

/** Métodos de pago activos para la corrección. */
export function useCatalogosMetodosPago() {
  return useQuery({
    queryKey: [CLAVE, 'turnos', 'metodos-pago-catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosMetodosPago(signal),
    staleTime: 300_000,
  });
}

/** Ventas de GNV del periodo. */
export function useVentasGnv(filtros: FiltrosVentasGnv) {
  return useQuery({
    queryKey: [CLAVE, 'ventas-gnv', filtros],
    queryFn: ({ signal }) => reportesApi.ventasGnv(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Ventas cobradas con un método de pago del periodo. */
export function useVentasPorMetodo(filtros: FiltrosVentasPorMetodo) {
  return useQuery({
    queryKey: [CLAVE, 'ventas-por-metodo', filtros],
    queryFn: ({ signal }) => reportesApi.ventasPorMetodo(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Fila del reporte de facturas pendientes de pago (una factura por cobrar). */
export interface FilaFacturaPendiente {
  comprobante_id: number;
  fecha_emision: string | null;
  fecha_vencimiento: string | null;
  cliente: string | null;
  documento: string | null;
  /** `serie-numero` (como lo mostraba el panel anterior). */
  factura: string;
  serie: string | null;
  numero: string | null;
  observaciones: string | null;
  condicion_pago: string | null;
  costo_comprobante: number;
  total: number;
  retencion: number;
  abonado: number;
  /** El saldo por cobrar (`admin_venta.pending_amount`). */
  pendiente: number;
  /** Días desde el vencimiento (negativo si todavía no vence). */
  dias_vencimiento: number | null;
  /** Vencida y con saldo por cobrar. */
  vencida: boolean;
}

/** Totales del reporte de facturas pendientes de pago. */
export interface ResumenFacturasPendientes {
  facturas: number;
  total: number;
  retencion: number;
  abonado: number;
  pendiente: number;
  vencidas: number;
  pendiente_vencido: number;
}

/** Las facturas por cobrar de un cliente (la tarjeta del panel anterior). */
export interface FacturaPendientePorCliente {
  documento: string | null;
  cliente: string | null;
  facturas: number;
  total: number;
  retencion: number;
  abonado: number;
  pendiente: number;
  vencidas: number;
}

/** Filtros del reporte de facturas pendientes de pago. */
export interface FiltrosFacturasPendientes {
  desde: string;
  hasta: string;
  /** RUC o DNI del cliente (vacío = todos). */
  numero_doc: string;
  /** Sólo las facturas cuyo abonado no cubre el saldo (`paidout < pending_amount`). */
  solo_pendientes: boolean;
  /** Sólo las vencidas (`fecha_vencimiento < hoy`). */
  vencidas: boolean;
}

/** Respuesta del reporte de facturas pendientes de pago. */
export interface FacturasPendientesRespuesta {
  filas: FilaFacturaPendiente[];
  por_cliente: FacturaPendientePorCliente[];
  resumen: ResumenFacturasPendientes;
  filtros: {
    desde: string;
    hasta: string;
    numero_doc: string | null;
    solo_pendientes: boolean;
    vencidas: boolean;
  };
  generado_at: string;
}

/** Catálogo de clientes con facturas por cobrar (el buscador del reporte). */
export interface CatalogosFacturasPendientes {
  clientes: { documento: string | null; cliente: string | null; facturas: number; pendiente: number }[];
  generado_at: string;
}

/** Facturas pendientes de pago del periodo. */
export function useFacturasPendientes(filtros: FiltrosFacturasPendientes) {
  return useQuery({
    queryKey: [CLAVE, 'facturas-pendientes', filtros],
    queryFn: ({ signal }) => reportesApi.facturasPendientes(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Clientes con facturas por cobrar en el rango. */
export function useCatalogosFacturasPendientes(rango: { desde: string; hasta: string }) {
  return useQuery({
    queryKey: [CLAVE, 'facturas-pendientes', 'catalogos', rango],
    queryFn: ({ signal }) => reportesApi.catalogosFacturasPendientes(rango, signal),
    staleTime: 300_000,
  });
}

/** Fila del reporte de comprobantes por serie, producto y fecha. */
export interface FilaSerieFechaProducto {
  venta_id: number;
  fecha: string;
  comprobante: string;
  serie: string | null;
  /** FACTURA, BOLETA, ADMINISTRADO o NOTA DE CREDITO. */
  origen: string;
  cliente: string | null;
  producto: string | null;
  producto_id: number | null;
  precio_venta: number;
  cantidad: number;
  precio_unidad: number;
  subtotal: number;
  descuento: number;
  total: number;
  grifero: string | null;
  estado: string | null;
  /** El comprobante está anulado: la cantidad y el total viajan en cero. */
  anulado: boolean;
}

/** Los totales por producto y por serie del reporte. */
export interface ProductoSerieFechaProducto {
  producto_id: number | null;
  producto: string | null;
  lineas: number;
  cantidad: number;
  subtotal: number;
  total: number;
}

/** Los totales de una serie del reporte. */
export interface SerieSerieFechaProducto {
  serie: string | null;
  origen: string;
  comprobantes: number;
  lineas: number;
  cantidad: number;
  total: number;
}

/** Totales del reporte de comprobantes por serie. */
export interface ResumenSerieFechaProducto {
  lineas: number;
  comprobantes: number;
  cantidad: number;
  subtotal: number;
  total: number;
  anuladas: number;
}

/** Filtros del reporte de comprobantes por serie, producto y fecha. */
export interface FiltrosSerieFechaProducto {
  desde: string;
  hasta: string;
  /** Serie exacta (vacío = todas). */
  serie: string;
  /** Producto exacto (vacío = todos). */
  producto: string;
  /** Incluir los comprobantes que no están aceptados (por defecto sólo aceptados). */
  todos: boolean;
}

/** Respuesta del reporte de comprobantes por serie, producto y fecha. */
export interface SerieFechaProductoRespuesta {
  filas: FilaSerieFechaProducto[];
  por_producto: ProductoSerieFechaProducto[];
  por_serie: SerieSerieFechaProducto[];
  resumen: ResumenSerieFechaProducto;
  filtros: {
    desde: string;
    hasta: string;
    serie: string | null;
    producto: number | null;
    todos: boolean;
  };
  generado_at: string;
}

/** Las series y los productos del reporte (los desplegables). */
export interface CatalogosSerieFechaProducto {
  series: { serie: string | null; origen: string; comprobantes: number }[];
  productos: { id: number; nombre: string; tipo: string | null; lineas: number }[];
  /** Meses con líneas (el atajo del rango). */
  meses: { periodo: string; lineas: number }[];
  generado_at: string;
}

/** Comprobantes por serie, producto y fecha. */
export function useSerieFechaProducto(filtros: FiltrosSerieFechaProducto) {
  return useQuery({
    queryKey: [CLAVE, 'serie-fecha-producto', filtros],
    queryFn: ({ signal }) => reportesApi.serieFechaProducto(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Series y productos del reporte de comprobantes por serie. */
export function useCatalogosSerieFechaProducto() {
  return useQuery({
    queryKey: [CLAVE, 'serie-fecha-producto', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosSerieFechaProducto(signal),
    staleTime: 300_000,
  });
}

/** Un movimiento del kardex (compra, venta de surtidor, salida o ajuste). */
export interface MovimientoKardex {
  /** `AAAA-MM-DD HH:MM:SS`. */
  fecha: string;
  tipo: 'COMPRA' | 'ENTRADA' | 'VENTA' | 'SALIDA' | 'AJUSTE' | string;
  comprobante: string | null;
  concepto: string | null;
  volumen: number;
  precio_unitario: number | null;
  importe: number | null;
  /** 1 si suma al saldo (entradas) y -1 si resta (ventas, salidas). */
  signo: number;
  /** Saldo acumulado del producto después de este movimiento. */
  saldo: number;
}

/** Saldos y totales del kardex. */
export interface ResumenKardex {
  saldo_inicial: number;
  entradas: number;
  entradas_importe: number;
  ventas: number;
  ventas_importe: number;
  salidas: number;
  ajustes: number;
  saldo_final: number;
  costo_promedio: number | null;
  valor_final: number | null;
  movimientos: number;
  dias: number;
}

/** Filtros del kardex. */
export interface FiltrosKardex {
  desde: string;
  hasta: string;
  /** `pts_fuel_grades.Id` del producto de surtidor. */
  producto: string;
  /** `individual` (entradas de almacén) o `valorizado` (compras con su costo). */
  vista: 'individual' | 'valorizado';
}

/** Respuesta del kardex. */
export interface KardexRespuesta {
  producto: { id: number; nombre: string; precio: number; producto_id: number | null };
  vista: 'individual' | 'valorizado';
  filas: MovimientoKardex[];
  resumen: ResumenKardex;
  filtros: { desde: string; hasta: string; producto: number; vista: string };
  generado_at: string;
}

/** Los productos de surtidor y el rango con movimientos. */
export interface CatalogosKardex {
  productos: {
    id: number;
    nombre: string;
    precio: number;
    producto_id: number | null;
    transacciones: number;
  }[];
  rango: { desde: string | null; hasta: string | null };
  generado_at: string;
}

/** Kardex del producto (individual o valorizado). */
export function useKardex(filtros: FiltrosKardex) {
  return useQuery({
    queryKey: [CLAVE, 'kardex', filtros],
    queryFn: ({ signal }) => reportesApi.kardex(filtros, signal),
    staleTime: 60_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Productos de surtidor del kardex. */
export function useCatalogosKardex() {
  return useQuery({
    queryKey: [CLAVE, 'kardex', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosKardex(signal),
    staleTime: 300_000,
  });
}

/** Una línea de crédito (vale del grifo o venta al crédito de la APP). */
export interface FilaCreditoCliente {
  fecha: string | null;
  cliente: string | null;
  producto: string | null;
  cantidad: number;
  /** Precio de lista (unitario más el descuento de la línea). */
  regular: number;
  precio: number;
  total: number;
  descuento: number;
  adelanto: number;
  /** Lo pagado al crédito en el vale. */
  pagado: number;
  vale: string | null;
  estado: string | null;
  serie: string | null;
  numero: string | null;
  /** `serie-numero` del comprobante, o la serie del vale si no tiene número. */
  comprobante: string | null;
  placa: string | null;
  conductor: string | null;
  conductor_dni: string | null;
  kilometraje: number | null;
  personal: string | null;
  /** Comprobante electrónico con el que se facturó el vale. */
  cpe: string | null;
  /** Saldo del comprobante (sólo en la primera línea de cada CPE). */
  pendiente: number | null;
  retencion: number | null;
  pagado_admin: number | null;
  /** `CREDITO` (vale del grifo) o `APP` (venta al crédito de la aplicación). */
  fuente: string;
  /** Lo que falta por cobrar de la línea. */
  por_cobrar: number;
}

/** Totales del reporte de créditos por cliente. */
export interface ResumenCreditosCliente {
  filas: number;
  comprobantes: number;
  clientes: number;
  productos: number;
  cantidad: number;
  regular: number;
  total: number;
  descuento: number;
  adelanto: number;
  pagado: number;
  /** Saldo de los comprobantes CPE asociados a los vales del filtro. */
  saldo_cpe: number;
  por_cobrar: number;
  retencion: number;
  con_pendiente: number;
}

/** Los créditos de un cliente. */
export interface CreditoPorCliente {
  cliente: string | null;
  filas: number;
  total: number;
  por_cobrar: number;
  /** Saldo de los comprobantes facturados. */
  pendiente: number;
  placas: number;
}

/** El estado del vale (el `tipo_credito` del panel anterior). */
export interface EstadoDeCredito {
  valor: number;
  etiqueta: string;
}

/** Filtros del reporte de créditos por cliente. */
export interface FiltrosCreditosCliente {
  desde: string;
  hasta: string;
  /** RUC o DNI del cliente (vacío = todos). */
  numero_doc: string;
  /** 1 todos, 2 sin facturar, 3 facturado, 4 pagados, 5 pendiente de pago. */
  estado: number;
  /** Placa (búsqueda parcial). */
  placa: string;
}

/** Respuesta del reporte de créditos por cliente. */
export interface CreditosClienteRespuesta {
  filas: FilaCreditoCliente[];
  por_cliente: CreditoPorCliente[];
  resumen: ResumenCreditosCliente;
  filtros: {
    desde: string;
    hasta: string;
    numero_doc: string | null;
    estado: number;
    placa: string | null;
  };
  generado_at: string;
}

/** Catálogos del reporte de créditos (clientes, placas, estados y rango). */
export interface CatalogosCreditosCliente {
  clientes: { documento: string | null; cliente: string | null; creditos: number; por_cobrar: number }[];
  placas: { placa: string | null; creditos: number }[];
  estados: EstadoDeCredito[];
  rango: { desde: string | null; hasta: string | null };
  generado_at: string;
}

/** Créditos por cliente del periodo. */
export function useCreditosCliente(filtros: FiltrosCreditosCliente) {
  return useQuery({
    queryKey: [CLAVE, 'creditos-cliente', filtros],
    queryFn: ({ signal }) => reportesApi.creditosCliente(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Clientes, placas y estados del vale del reporte de créditos. */
export function useCatalogosCreditosCliente() {
  return useQuery({
    queryKey: [CLAVE, 'creditos-cliente', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosCreditosCliente(signal),
    staleTime: 300_000,
  });
}

/** Un turno del reporte de faltantes del personal. */
export interface FilaFaltantePersonal {
  turno_id: number;
  fecha: string | null;
  llegada: string | null;
  salida: string | null;
  dinero_llegada: number;
  /** Lo ingresado a bóveda en el turno (tabla `boveda`). */
  ingreso_boveda: number;
  entregado: number;
  usuario_id: number | null;
  dni: string | null;
  trabajador: string | null;
  /** El turno ya se verificó. */
  validado: boolean;
  fecha_verificacion: string | null;
  /** Lo que quedó pendiente de entregar según la verificación. */
  faltante: number | null;
  detalle: string | null;
  tipo_verificacion: string | null;
}

/** Totales del reporte de faltantes. */
export interface ResumenFaltantesPersonal {
  turnos: number;
  trabajadores: number;
  validadas: number;
  sin_verificar: number;
  con_faltante: number;
  faltante: number;
  ingreso_boveda: number;
  dias: number;
}

/** El faltante de un trabajador. */
export interface FaltantePorTrabajador {
  usuario_id: number | null;
  trabajador: string | null;
  dni: string | null;
  turnos: number;
  validadas: number;
  faltante: number;
  ingreso_boveda: number;
}

/** El faltante de un mes (la ficha es «por mes»). */
export interface FaltantePorMes {
  periodo: string;
  turnos: number;
  validadas: number;
  faltante: number;
}

/** Filtros del reporte de faltantes del personal. */
export interface FiltrosFaltantesPersonal {
  desde: string;
  hasta: string;
  /** Id del trabajador (vacío = todos). */
  usuario: string;
}

/** Respuesta del reporte de faltantes del personal. */
export interface FaltantesPersonalRespuesta {
  filas: FilaFaltantePersonal[];
  por_trabajador: FaltantePorTrabajador[];
  por_mes: FaltantePorMes[];
  resumen: ResumenFaltantesPersonal;
  filtros: { desde: string; hasta: string; usuario: number | null };
  generado_at: string;
}

/** Catálogos del reporte de faltantes (trabajadores con turnos y meses). */
export interface CatalogosFaltantesPersonal {
  trabajadores: { id: number; trabajador: string | null; dni: string | null; turnos: number }[];
  meses: { periodo: string; turnos: number }[];
  rango: { desde: string | null; hasta: string | null };
  generado_at: string;
}

/** Faltante del personal del periodo. */
export function useFaltantesPersonal(filtros: FiltrosFaltantesPersonal) {
  return useQuery({
    queryKey: [CLAVE, 'faltantes-personal', filtros],
    queryFn: ({ signal }) => reportesApi.faltantesPersonal(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Trabajadores con turnos y meses del reporte de faltantes. */
export function useCatalogosFaltantesPersonal() {
  return useQuery({
    queryKey: [CLAVE, 'faltantes-personal', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosFaltantesPersonal(signal),
    staleTime: 300_000,
  });
}

/** Un descuento o recargo vigente de un cliente. */
export interface ItemDescuentoActivo {
  id: number;
  /** `PUNTO DE VENTA` (customer_discounts) o `CREDITO` (el descuento del crédito). */
  fuente: string;
  producto_id: number | null;
  producto: string | null;
  /** Importe **por unidad** (por galón o por documento), no el total facturado. */
  descuento: number;
  tipo_descuento: string;
  /** `descuento` o `recargo` (el importe venía en negativo). */
  naturaleza: string;
  placa: string | null;
  vehicle_id: number | null;
  vigente_hasta: string | null;
  detalle: string | null;
}

/** Los descuentos activos de un cliente. */
export interface ClienteConDescuentos {
  cliente_id: number;
  documento: string | null;
  cliente: string | null;
  /** Las placas que aparecen en sus descuentos por placa. */
  placas: string[];
  /** Todas las placas activas del cliente (`customer_vehicles`). */
  placas_activas: string[];
  items: ItemDescuentoActivo[];
  descuentos: number;
  recargos: number;
}

/** Totales del reporte de descuentos activos. */
export interface ResumenDescuentosActivos {
  clientes: number;
  items: number;
  descuentos: number;
  recargos: number;
  placas: number;
  descuento_promedio: number;
  recargo_promedio: number;
}

/** Filtros del reporte de descuentos activos. */
export interface FiltrosDescuentosActivos {
  /** Id del cliente (vacío = todos). */
  cliente: string;
  /** `PUNTO DE VENTA` o `CREDITO`. */
  fuentes: string[];
  /** `document`, `license_plate`, `consumption`, `credito` o `recargo`. */
  tipos: string[];
}

/** Respuesta del reporte de descuentos activos. */
export interface DescuentosActivosRespuesta {
  clientes: ClienteConDescuentos[];
  por_fuente: { fuente: string; items: number; suma: number }[];
  por_tipo: { tipo: string; items: number; suma: number }[];
  resumen: ResumenDescuentosActivos;
  filtros: { cliente: number | null; fuentes: string[]; tipos: string[] };
  generado_at: string;
}

/** Catálogos del reporte de descuentos activos. */
export interface CatalogosDescuentosActivos {
  clientes: { id: number; documento: string | null; cliente: string | null; items: number }[];
  fuentes: { valor: string; etiqueta: string }[];
  tipos: { valor: string; etiqueta: string; items: number }[];
  generado_at: string;
}

/** Descuentos y recargos vigentes del periodo. */
export function useDescuentosActivos(filtros: FiltrosDescuentosActivos) {
  return useQuery({
    queryKey: [CLAVE, 'descuentos-activos', filtros],
    queryFn: ({ signal }) => reportesApi.descuentosActivos(filtros, signal),
    staleTime: 60_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Clientes, fuentes y tipos del reporte de descuentos activos. */
export function useCatalogosDescuentosActivos() {
  return useQuery({
    queryKey: [CLAVE, 'descuentos-activos', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosDescuentosActivos(signal),
    staleTime: 300_000,
  });
}

/** Una línea de venta de un turno (producto y serie del comprobante). */
export interface FilaProductividad {
  fecha: string;
  turno: string;
  producto: string | null;
  serie: string | null;
  /** `TURNO` (turno.transactions) o `MARKET` (market_document_response). */
  tipo: string;
  ventas: number;
  monto: number;
  volumen: number;
}

/** Los totales de un turno. */
export interface TotalesProductividad {
  ventas: number;
  monto: number;
  volumen: number;
}

/** Un turno de un día con sus productos. */
export interface TurnoDelDia {
  turno: string;
  productos: FilaProductividad[];
  totales: TotalesProductividad;
  /** Monto entre volumen del turno (galones). */
  precio_medio: number;
}

/** Un día del reporte. */
export interface DiaProductividad {
  fecha: string;
  turnos: TurnoDelDia[];
  totales: TotalesProductividad;
  /** El turno que más vendió ese día. */
  mejor_turno: string | null;
}

/** La productividad de un turno en el periodo. */
export interface ProductividadPorTurno {
  turno: string;
  dias: number;
  ventas: number;
  monto: number;
  volumen: number;
  /** Su venta media por día trabajado. */
  promedio_dia: number;
}

/** Totales del reporte de productividad. */
export interface ResumenProductividad {
  dias: number;
  turnos: number;
  ventas: number;
  monto: number;
  volumen: number;
  market_monto: number;
  market_ventas: number;
  promedio_turno: number;
}

/** Filtros del reporte de productividad por turnos (máximo 90 días). */
export interface FiltrosProductividad {
  desde: string;
  hasta: string;
  /** Id del turno de `shift_cat` (vacío = todos). */
  turno: string;
  /** Producto exacto del surtidor (vacío = todos). */
  producto: string;
  /** Serie del comprobante (vacío = todas). */
  serie: string;
}

/** Respuesta del reporte de productividad por turnos. */
export interface ProductividadRespuesta {
  filas: FilaProductividad[];
  dias: DiaProductividad[];
  por_turno: ProductividadPorTurno[];
  por_producto: { producto: string | null; ventas: number; monto: number; volumen: number }[];
  por_serie: { serie: string | null; ventas: number; monto: number }[];
  resumen: ResumenProductividad;
  filtros: {
    desde: string;
    hasta: string;
    dias: number;
    turno: number | null;
    producto: string | null;
    serie: string | null;
  };
  generado_at: string;
}

/** Catálogos del reporte de productividad. */
export interface CatalogosProductividad {
  turnos: { id: number; turno: string | null; inicio: string; fin: string }[];
  /** Meses con transacciones (el atajo del rango). */
  meses: { periodo: string; dias: number }[];
  productos: { producto: string | null; ventas: number; monto: number }[];
  series: { serie: string | null; ventas: number; monto: number }[];
  rango: { desde: string | null; hasta: string | null };
  /** El tope de días que acepta el reporte. */
  maximo_dias: number;
  generado_at: string;
}

/** Productividad de los turnos del periodo. */
export function useProductividadTurnos(filtros: FiltrosProductividad) {
  return useQuery({
    queryKey: [CLAVE, 'productividad-turnos', filtros],
    queryFn: ({ signal }) => reportesApi.productividadTurnos(filtros, signal),
    staleTime: 60_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Turnos, productos y series del reporte de productividad. */
export function useCatalogosProductividad() {
  return useQuery({
    queryKey: [CLAVE, 'productividad-turnos', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosProductividad(signal),
    staleTime: 300_000,
  });
}

/** Un movimiento bancario (movements_account). */
export interface FilaMovimientoCuenta {
  id: number;
  fecha: string | null;
  /** Cuándo se registró el movimiento. */
  registrado_at: string | null;
  banco: string | null;
  cuenta: string | null;
  numero_cuenta: string | null;
  /** El tipo de movimiento (`abc_tipo_movimiento`). */
  tipo: string | null;
  tipo_id: number;
  subtipo_id: number | null;
  /** El método de pago (`abc_metodo_pago`). */
  metodo: string | null;
  /** `INGRESO` o `EGRESO` (lo define el tipo de movimiento). */
  direccion: string | null;
  monto: number;
  /** El archivo de sustento (imagen o documento). */
  archivo: string | null;
  referencia: string | null;
  notas: string | null;
  estado: string;
  /** Resumen legible de los pagos aplicados (`movement_detail`). */
  detalle: string | null;
  cliente: string | null;
  documento: string | null;
  ejecutor: string | null;
  anulado_por: string | null;
  actualizado_at: string | null;
}

/** Totales del reporte de movimientos. */
export interface ResumenMovimientosCuenta {
  movimientos: number;
  monto: number;
  ingresos: number;
  egresos: number;
  registrados: number;
  pendientes: number;
  con_archivo: number;
}

/** El movimiento de una cuenta. */
export interface MovimientoPorCuenta {
  banco: string | null;
  cuenta: string | null;
  numero_cuenta: string | null;
  movimientos: number;
  ingresos: number;
  egresos: number;
  saldo: number;
}

/** Filtros del reporte de movimientos en cuenta. */
export interface FiltrosMovimientosCuenta {
  desde: string;
  hasta: string;
  /** Id del banco (vacío = todos). */
  banco: string;
  /** Id de la cuenta (vacío = todas). */
  cuenta: string;
  /** `vigentes` (registrados y pendientes) o `anulados`. */
  estado: 'vigentes' | 'anulados';
}

/** Respuesta del reporte de movimientos en cuenta. */
export interface MovimientosCuentaRespuesta {
  filas: FilaMovimientoCuenta[];
  por_cuenta: MovimientoPorCuenta[];
  por_tipo: { tipo: string | null; movimientos: number; monto: number }[];
  resumen: ResumenMovimientosCuenta;
  filtros: {
    desde: string;
    hasta: string;
    banco: number | null;
    cuenta: number | null;
    estado: string;
  };
  generado_at: string;
}

/** Catálogos del reporte de movimientos (bancos, cuentas, métodos y meses). */
export interface CatalogosMovimientosCuenta {
  bancos: { id: number; banco: string | null; cuentas: number }[];
  cuentas: {
    id: number;
    cuenta: string | null;
    numero: string | null;
    banco_id: number;
    banco: string | null;
    saldo_inicial: number;
    movimientos: number;
  }[];
  metodos: { id: number; metodo: string | null; tipo: string | null; movimientos: number }[];
  estados: { valor: string; etiqueta: string }[];
  meses: { periodo: string; movimientos: number }[];
  rango: { desde: string | null; hasta: string | null };
  generado_at: string;
}

/** Movimientos bancarios del periodo. */
export function useMovimientosCuenta(filtros: FiltrosMovimientosCuenta) {
  return useQuery({
    queryKey: [CLAVE, 'movimientos-cuenta', filtros],
    queryFn: ({ signal }) => reportesApi.movimientosCuenta(filtros, signal),
    staleTime: 60_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Bancos, cuentas y métodos del reporte de movimientos. */
export function useCatalogosMovimientosCuenta() {
  return useQuery({
    queryKey: [CLAVE, 'movimientos-cuenta', 'catalogos'],
    queryFn: ({ signal }) => reportesApi.catalogosMovimientosCuenta(signal),
    staleTime: 300_000,
  });
}

/** Una factura pagada por adelantado (con su documento de pago). */
export interface PagoAdelantado {
  id: number;
  fecha: string | null;
  registrado_at: string | null;
  /** RUC o DNI del cliente. */
  numero_doc: string | null;
  cliente: string | null;
  serie: string | null;
  numero: number | null;
  /** `serie-numero` con el número a seis dígitos. */
  comprobante: string;
  external_id: string | null;
  estado_sunat: string | null;
  /** Nombre del archivo del documento de pago (null si no se ha subido). */
  documento: string | null;
  total: number;
  productos: number;
  /** Cantidad comprada, entregada y pendiente de entrega de la factura global. */
  comprado: number;
  entregado: number;
  pendiente: number;
}

/** Totales del listado de pagos adelantados. */
export interface ResumenPagosAdelantados {
  ventas: number;
  con_documento: number;
  sin_documento: number;
  pendientes: number;
  total: number;
  pendiente_entrega: number;
}

/** Filtros del listado de pagos adelantados. */
export interface FiltrosPagosAdelantados {
  desde: string;
  hasta: string;
  /** Busca por número de comprobante, documento o nombre del cliente. */
  busqueda: string;
  /** Sólo las facturas a las que les falta el documento de pago. */
  sin_documento: boolean;
}

/** Respuesta del listado de pagos adelantados. */
export interface PagosAdelantadosRespuesta {
  ventas: PagoAdelantado[];
  resumen: ResumenPagosAdelantados;
  generado_at: string;
}

/** El detalle de una factura pagada por adelantado. */
export interface DetallePagoAdelantado {
  venta: number;
  productos: {
    producto: string | null;
    comprado: number;
    entregado: number;
    pendiente: number;
    precio: number;
    total: number;
  }[];
  /** Las entregas hechas con notas de venta. */
  entregas: {
    fecha: string | null;
    comprobante: string | null;
    cantidad: number;
    producto: string | null;
    nota_venta_id: number;
  }[];
  documento: string | null;
  generado_at: string;
}

/** Listado de facturas pagadas por adelantado. */
export function usePagosAdelantados(filtros: FiltrosPagosAdelantados) {
  return useQuery({
    queryKey: [CLAVE, 'pagos-adelantados-documentos', filtros],
    queryFn: ({ signal }) => reportesApi.pagosAdelantados(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** El detalle de una factura pagada por adelantado. */
export function useDetallePagoAdelantado(venta: number | null) {
  return useQuery({
    queryKey: [CLAVE, 'pagos-adelantados-documentos', 'detalle', venta],
    queryFn: ({ signal }) => reportesApi.detallePagoAdelantado(venta as number, signal),
    enabled: venta !== null,
    staleTime: 30_000,
  });
}

/* -------------------------------------------------------------------------- *
 * Nubecont detallado (API del proveedor)
 * -------------------------------------------------------------------------- */

/** Una fila del listado del proveedor, con las columnas de su exportación. */
export interface FilaNubecont {
  fecha_emision: string | null;
  fecha_vencimiento: string | null;
  tipo_documento: string | null;
  serie: string | null;
  numero: string | null;
  tipo_documento_cliente: string;
  numero_documento_cliente: string | null;
  razon_social: string | null;
  moneda: string | null;
  tipo_cambio: number | null;
  porcentaje_igv: string;
  exportacion: string;
  gravada: number | null;
  exonerada: string;
  inafecta: string;
  isc: string;
  igv: number | null;
  otros_tributos: string;
  icbper: string;
  total: number | null;
  detraccion: string;
  importe_detraccion: string;
  detalle: string;
  anulado: string;
  consolidado: string;
  codigo_tipo_venta: string;
}

/** Totales del listado del proveedor. */
export interface ResumenNubecont {
  comprobantes: number;
  anulados: number;
  clientes: number;
  gravada: number;
  igv: number;
  total: number;
}

/** Filtros del reporte de Nubecont. */
export interface FiltrosNubecont {
  desde: string;
  hasta: string;
}

/** Respuesta del reporte de Nubecont. */
export interface NubecontRespuesta {
  filtros: { inicio: string; fin: string };
  origen: { proveedor: string; url: string };
  columnas: string[];
  filas: FilaNubecont[];
  resumen: ResumenNubecont;
  generado_at: string;
}

/**
 * Listado detallado del proveedor Nubecont.
 *
 * La consulta va a la API externa (dirección y token de Configuración → Empresa),
 * así que un fallo de red no se reintenta solo: la pantalla enseña el motivo.
 */
export function useNubecont(filtros: FiltrosNubecont, habilitado = true) {
  return useQuery({
    queryKey: [CLAVE, 'nubecont', filtros],
    queryFn: ({ signal }) => reportesApi.nubecont(filtros, signal),
    enabled: habilitado,
    staleTime: 60_000,
    retry: false,
  });
}
